/**
 * Types pour la gestion des clients et leurs membres
 */

export type ClientType = 'pharmacie' | 'parapharmacie' | 'centrale' | 'magasin_bio' | 'therapeute' | 'member' | 'independent' | 'central';

// Nouveaux types de membres
export type MemberType = 'comptable' | 'responsable' | 'gerant' | 'employe';

// Anciens types pour compatibilité (seront migrés)
export type MemberRole = MemberType | 'manager' | 'accountant' | 'viewer' | 'purchaser' | 'independent';

export interface ClientAdminLite {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

export interface Client {
  id: number;
  company_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string;
  is_active: boolean;
  client_type: ClientType;
  parent_client_id: number | null;  // ID de la centrale d'achat parente (si member)
  bpu_locked: boolean;  // BPU verrouillé (hérité d'une centrale)
  admin_id: number;
  admin?: ClientAdminLite | null;
  created_at: string;
  updated_at: string;
  last_login: string | null;
  // Informations de facturation
  payment_mode: string | null;
  payment_terms: string | null;
  invoice_email: string | null;
  central_purchasing_id?: number | null;
  use_central_purchasing_bpu?: boolean;
}

export interface ClientCreate {
  company_name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  password: string;
  is_active?: boolean;
  client_type?: ClientType;
  parent_client_id?: number;  // ID de la centrale parente (si type = member)
  admin_id?: number;
  // Informations de facturation
  payment_mode?: string;
  payment_terms?: string;
  invoice_email?: string;
  central_purchasing_id?: number | null;
  use_central_purchasing_bpu?: boolean;
}

export interface ClientUpdate {
  company_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  password?: string;
  is_active?: boolean;
  client_type?: ClientType;
  parent_client_id?: number;
  admin_id?: number;
  // Informations de facturation
  payment_mode?: string;
  payment_terms?: string;
  invoice_email?: string;
  central_purchasing_id?: number | null;
  use_central_purchasing_bpu?: boolean;
}

export interface ClientMember {
  id: number;
  client_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  is_primary?: boolean;
  member_type: MemberRole;  // Type de membre (nouveau champ)
  access_all_sales_points: boolean;
  can_create_sales_points: boolean;
  can_create_orders: boolean;
  can_manage_invoices: boolean;
  receive_notifications: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login: string | null;
  notes: string | null;
  sales_point_ids: number[];
}

export interface ClientMemberCreate {
  client_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  is_primary?: boolean;
  password: string;
  member_type?: MemberRole;  // Type de membre
  access_all_sales_points?: boolean;
  sales_point_ids?: number[];
  can_create_sales_points?: boolean;
  can_create_orders?: boolean;
  can_manage_invoices?: boolean;
  receive_notifications?: boolean;
  is_active?: boolean;
  notes?: string;
  send_welcome_email?: boolean;
}

export interface ClientMemberUpdate {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  is_primary?: boolean;
  password?: string;
  member_type?: MemberRole;
  access_all_sales_points?: boolean;
  sales_point_ids?: number[];
  can_create_sales_points?: boolean;
  can_create_orders?: boolean;
  can_manage_invoices?: boolean;
  receive_notifications?: boolean;
  is_active?: boolean;
  notes?: string;
  send_welcome_email?: boolean;
}

export interface CentralPricing {
  id: number;
  central_client_id: number;
  consumable_id: number;
  negotiated_price: number;
  created_at: string;
  updated_at: string;
  notes: string | null;
}

export interface CentralPricingCreate {
  central_client_id: number;
  consumable_id: number;
  negotiated_price: number;
  notes?: string;
}

export interface CentralPricingUpdate {
  negotiated_price?: number;
  notes?: string;
}

export interface CentralMembership {
  id: number;
  central_client_id: number;
  member_client_id: number;
  created_at: string;
}

export interface CentralMembershipCreate {
  central_client_id: number;
  member_client_id: number;
}

export interface ClientStats {
  independent: number;
  central: number;
  member: number;
}

// Labels pour l'affichage
export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  pharmacie: 'Pharmacie',
  parapharmacie: 'Parapharmacie',
  centrale: 'Centrale d\'Achat',
  magasin_bio: 'Magasin Bio',
  therapeute: 'Thérapeute',
  member: 'Client Membre',
  // Backward compatibility
  independent: 'Indépendant',
  central: 'Centrale d\'Achat',
};

// Labels pour les nouveaux types de membres
export const MEMBER_TYPE_LABELS: Record<MemberType, string> = {
  comptable: 'Comptable',
  responsable: 'Responsable',
  gerant: 'Gérant',
  employe: 'Employé',
};

// Labels pour compatibilité avec anciens rôles
export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  // Nouveaux types
  comptable: 'Comptable',
  responsable: 'Responsable',
  gerant: 'Gérant',
  employe: 'Employé',
  // Anciens types (compatibilité)
  manager: 'Responsable',
  accountant: 'Comptable',
  viewer: 'Consultation',
  purchaser: 'Client',
  independent: 'Indépendant',
};

// Couleurs pour les badges
export const CLIENT_TYPE_COLORS: Record<ClientType, string> = {
  pharmacie: 'green',
  parapharmacie: 'teal',
  centrale: 'orange',
  magasin_bio: 'lime',
  therapeute: 'purple',
  member: 'cyan',
  // Backward compatibility
  independent: 'blue',
  central: 'orange',
};

// Couleurs pour les nouveaux types de membres
export const MEMBER_TYPE_COLORS: Record<MemberType, string> = {
  comptable: 'blue',
  responsable: 'purple',
  gerant: 'green',
  employe: 'gray',
};

// Couleurs pour compatibilité avec anciens rôles
export const MEMBER_ROLE_COLORS: Record<MemberRole, string> = {
  // Nouveaux types
  comptable: 'blue',
  responsable: 'purple',
  gerant: 'green',
  employe: 'gray',
  // Anciens types (compatibilité)
  manager: 'purple',
  accountant: 'blue',
  viewer: 'gray',
  purchaser: 'orange',
  independent: 'green',
};
